import { isAxiosError } from 'axios';
import React, { useState } from 'react';
import {
  Div,
  Flex,
  Gutter,
  Title,
  BodyText,
  Input,
  GridBag,
  Button,
  Headline,
  Step,
  InlineNotification,
  Image,
  Checkbox,
  Switch,
  RadioButton,
  Loader,
} from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { AdminProduct, LocaleResponse, AdminProductEnvelope, UrlImage, AdminProductTypeEnum } from '@cdaas/api-clients/dist/product-service-openapi';

import { getAdminProductFromPopulatedProduct, getInitialAdminProduct } from '../../../utils/products';
import { productServiceAdminExternalProductsClient } from '../../../api-client/product-service-client';

import './ProductPopulate.scss';

interface ProductPopulateProps {
  initializeProductDataFromEnvelope: (productEnvelope: AdminProductEnvelope) => void;
  locales: LocaleResponse[];
  variationsExist: boolean;
  setVariationsExist: (exist: boolean) => void;
  setPopulateMode: (populateMode: boolean) => void;
}

enum CNET_ERROR {
  GENERAL,
  NOT_FOUND,
  TIMEOUT,
}

export default function ProductPopulate(props: ProductPopulateProps) {
  const { initializeProductDataFromEnvelope, locales, variationsExist, setVariationsExist, setPopulateMode } = props;
  const { t } = useTranslation('productscreen');

  const [type, setType] = useState<AdminProductTypeEnum>(AdminProductTypeEnum.Hardware);
  const [brand, setBrand] = useState<string>('');
  const [sku, setSku] = useState<string>('');

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [temporaryPrePopulatedProduct, setTemporaryPrePopulatedProduct] = useState<AdminProduct>();
  const [prePopulateRequestFailed, setPrePopulateRequestFailed] = useState<{ failed: boolean; reason?: CNET_ERROR }>({ failed: false });

  const [loading, setLoading] = useState<boolean>(false);

  const handleFetchPrePopulateData = async () => {
    setLoading(true);
    try {
      const languageTags = locales.map(locale => locale.language_tag);
      const response = await productServiceAdminExternalProductsClient.getExternalProduct(sku || '', brand || '', languageTags);
      setTemporaryPrePopulatedProduct(getAdminProductFromPopulatedProduct(response.data));
      setPrePopulateRequestFailed({ failed: false, reason: undefined });
    } catch (e) {
      let reason = CNET_ERROR.GENERAL;
      if (isAxiosError(e)) {
        switch (e.response?.status) {
          case 404:
            reason = CNET_ERROR.NOT_FOUND;
            break;
          case 504:
            reason = CNET_ERROR.TIMEOUT;
            break;
        }
      }
      setPrePopulateRequestFailed({ failed: true, reason });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewProductWithPrePopulate = async () => {
    if (temporaryPrePopulatedProduct) {
      initializeProductDataFromEnvelope({ product: { ...temporaryPrePopulatedProduct, type }, properties: [], property_values: [] });
      setCurrentStep(3);
    }
  };

  const handleCreateNewProductWithoutPrePopulate = async () => {
    const notPrepopulatedProduct = getInitialAdminProduct(brand, sku);
    initializeProductDataFromEnvelope({ product: { ...notPrepopulatedProduct, type }, properties: [], property_values: [] });
    setCurrentStep(3);
  };

  const steps = (
    <Div padding={{ size: 'md' }} boxShadow="sm">
      <Flex directionColumn occupyVerticalSpace={false}>
        <Title size="md">{t('populatescreen.steps_title')}</Title>
        <Gutter />
        <Flex directionColumn>
          <Step label={1} active={currentStep === 1} completed={currentStep > 1} completionIcon description={t('populatescreen.product_type_step')} />
          <Gutter />
          <Step label={2} active={currentStep === 2} completed={currentStep > 2} completionIcon description={t('populatescreen.fetch_data_step')} />
          <Gutter />
          <Step label={3} active={currentStep === 3} completed={currentStep > 3} completionIcon description={t('populatescreen.languages_step')} />
          <Gutter />
          <Step label={4} active={currentStep === 4} completed={currentStep > 4} completionIcon description={t('populatescreen.variants_step')} />
        </Flex>
      </Flex>
    </Div>
  );

  const productTypeForm = (
    <Div padding={{ size: 'md' }} boxShadow="sm">
      <Flex directionColumn occupyVerticalSpace={false}>
        <Title size="md">{t('populatescreen.product_type_title')}</Title>
        <Gutter />
        <BodyText size="md">{t('populatescreen.product_type_info')}</BodyText>
        <Gutter />
        <RadioButton
          label={
            <>
              {t('populatescreen.product_type_hardware')}{' '}
              <span style={{ fontStyle: 'italic' }}>{t('populatescreen.product_type_hardware_info')}</span>
            </>
          }
          checked={type === AdminProductTypeEnum.Hardware}
          onChange={() => setType(AdminProductTypeEnum.Hardware)}
        />
        <Gutter size="sm" />
        <RadioButton
          label={
            <>
              {t('populatescreen.product_type_service')} <span style={{ fontStyle: 'italic' }}>{t('populatescreen.product_type_service_info')}</span>
            </>
          }
          checked={type === AdminProductTypeEnum.Service}
          onChange={() => setType(AdminProductTypeEnum.Service)}
        />
        <Gutter size="xlg" />
        <Div>
          <Button variant="primary" size="lg" onClick={() => setCurrentStep(2)}>
            {t('populatescreen.buttons.next')}
          </Button>
        </Div>
      </Flex>
    </Div>
  );

  const fetchDataForm = (
    <Div padding={{ size: 'md' }} boxShadow="sm">
      <Flex directionColumn occupyVerticalSpace={false}>
        <Title size="md">{t('populatescreen.manufacturer_and_sku_title')}</Title>
        <Gutter />
        <BodyText size="md">{t('populatescreen.manufacturer_and_sku_info')}</BodyText>
        <Gutter />
        <GridBag>
          <GridBag.Item md={4}>
            <Input
              value={brand || ''}
              onChange={event => setBrand(event.target.value)}
              name="brand"
              label={t('populatescreen.brand_label')}
              required
            />
          </GridBag.Item>
          <GridBag.Item md={4}>
            <Input
              value={sku || ''}
              onChange={(event): void => setSku(event.target.value)}
              name="sku"
              label={t('populatescreen.sku_label')}
              required
            />
          </GridBag.Item>
        </GridBag>
      </Flex>
      <Gutter size="xlg" />
      <Div>
        {!temporaryPrePopulatedProduct && !prePopulateRequestFailed.failed ? (
          // Pre-population not attempted yet
          <Button variant="primary" size="lg" onClick={handleFetchPrePopulateData} disabled={!sku || !brand}>
            {t('populatescreen.buttons.next')}
          </Button>
        ) : prePopulateRequestFailed.failed ? (
          // Pre-population attempted and no product was found
          <Div>
            <Flex>
              <InlineNotification
                title={(() => {
                  switch (prePopulateRequestFailed.reason) {
                    case CNET_ERROR.NOT_FOUND:
                      return t('populatescreen.prepopulate_failed_product_not_found_info');
                    case CNET_ERROR.TIMEOUT:
                      return t('populatescreen.prepopulate_failed_timeout_info');
                    default:
                      return t('populatescreen.prepopulate_failed_general_info');
                  }
                })()}
                variant="error"
              />
            </Flex>
            <Gutter />
            <Flex centeredVertically>
              <BodyText size="md">{t('populatescreen.manual_entry_question')}</BodyText>
              <Gutter />
              <Button variant="secondary" size="lg" onClick={handleCreateNewProductWithoutPrePopulate} disabled={!sku || !brand}>
                {t('populatescreen.buttons.manual_entry')}
              </Button>
            </Flex>
            <Gutter size="sm" />
            <Flex centeredVertically>
              <BodyText size="md">
                {prePopulateRequestFailed.reason === CNET_ERROR.NOT_FOUND
                  ? t('populatescreen.product_not_found_try_prepopulate_again')
                  : t('populatescreen.general_try_prepopulate_again')}
              </BodyText>
              <Gutter />
              <Button variant="secondary" size="lg" onClick={handleFetchPrePopulateData} disabled={!sku || !brand}>
                {t('populatescreen.buttons.try_populate_again')}
              </Button>
            </Flex>
          </Div>
        ) : (
          // Pre-population attempted and product was found
          <Div>
            <BodyText color="purple500">{t('populatescreen.found_match')}</BodyText>
            <Flex centeredVertically margin={{ top: 'lg', right: 'md' }} padding={{ size: 'sm' }} backgroundColor="gray200">
              <Div flexItem margin={{ right: 'xs' }}>
                <Image
                  src={temporaryPrePopulatedProduct?.images?.[0] ? (temporaryPrePopulatedProduct?.images[0] as UrlImage).full_size_url : ''}
                  alt={t('populatescreen.product_image_alt')}
                  style={{ height: '128px', width: '128px' }}
                />
              </Div>
              <Div flexItem={{ sizeMultiplier: 3 }}>
                <Flex directionColumn>
                  <Title size="xxs">{temporaryPrePopulatedProduct?.name}</Title>
                  <BodyText>{temporaryPrePopulatedProduct?.key_selling_points.join(', ')}</BodyText>
                </Flex>
              </Div>
              <Div flexItem alignTextToCenter>
                <Button variant="primary" size="lg" onClick={handleCreateNewProductWithPrePopulate} disabled={!sku || !brand}>
                  {t('populatescreen.buttons.use_this')}
                </Button>
              </Div>
            </Flex>
            <Gutter />
            <Flex centeredVertically>
              <BodyText size="md">{t('populatescreen.not_what_you_need_question')}</BodyText>
              <Gutter />
              <Button variant="secondary" size="lg" onClick={handleCreateNewProductWithoutPrePopulate} disabled={!sku || !brand}>
                {t('populatescreen.buttons.manual_entry')}
              </Button>
            </Flex>
          </Div>
        )}
      </Div>
    </Div>
  );

  const removeParenthesizedSubstrings = (str: string): string =>
    str
      .split('(')
      .map((part, i) => (i === 0 ? part : part.split(')').slice(1).join(')')))
      .join('')
      .replace(/\s{2,}/g, ' ')
      .trim();

  // English is the language that should always be used as the reference - show that as well
  const languages = locales.map(locale => removeParenthesizedSubstrings(locale.name)).concat('English');
  const languagesInfo = (
    <Div padding={{ size: 'md' }} boxShadow="sm">
      <Flex directionColumn occupyVerticalSpace={false}>
        <Title size="md">{t('populatescreen.languages_title')}</Title>
        <Gutter />
        <BodyText size="md">{t('populatescreen.languages_info', { languages: languages.join(', ') })}</BodyText>
        <Gutter />
        {languages.map(language => (
          <React.Fragment key={language}>
            <Checkbox label={language} checked={true} disabled={true} />
            <Gutter />
          </React.Fragment>
        ))}
        <Gutter size="xlg" />
        <Div>
          <Button variant="primary" size="lg" onClick={() => setCurrentStep(4)}>
            {t('populatescreen.buttons.next')}
          </Button>
        </Div>
      </Flex>
    </Div>
  );

  const variantsSelection = (
    <Div padding={{ size: 'md' }} boxShadow="sm">
      <Flex directionColumn occupyVerticalSpace={false}>
        <Title size="md">{t('populatescreen.variants_title')}</Title>
        <Gutter />
        <BodyText size="md">{t('populatescreen.variants_info')}</BodyText>
        <Gutter />
        <Flex centeredVertically>
          <BodyText size="md">{t('populatescreen.variants_use')}</BodyText>
          <Gutter />
          <Switch
            isOn={variationsExist}
            label={variationsExist ? t('populatescreen.variants_yes') : t('populatescreen.variants_no')}
            onChange={() => setVariationsExist(!variationsExist)}
          />
        </Flex>
        <Gutter size="xlg" />
        <Div>
          <Button variant="primary" size="lg" onClick={() => setPopulateMode(false)}>
            {t('populatescreen.buttons.next')}
          </Button>
        </Div>
      </Flex>
    </Div>
  );

  return (
    <Div>
      <Headline size="lg">{t('populatescreen.prepopulate_product_title')}</Headline>
      <Gutter size="lg" />
      <GridBag>
        <GridBag.Item md={8} className="product-prepopulate-cnet-form">
          {loading && (
            <Div className="loading-overlay" alignTextToCenter>
              <Loader className="loader" size="lg" />
            </Div>
          )}
          {currentStep === 1 && productTypeForm}
          {currentStep === 2 && fetchDataForm}
          {currentStep === 3 && languagesInfo}
          {currentStep === 4 && variantsSelection}
        </GridBag.Item>
        <GridBag.Item md={2}>{steps}</GridBag.Item>
      </GridBag>
    </Div>
  );
}
